import React, {
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    DeleteOutlined,
    UploadOutlined,
    EditOutlined,
    PlusOutlined,
} from '@ant-design/icons'

import {
    Button,
    Card,
    Col,
    Divider,
    Form,
    Image,
    Input,
    InputNumber,
    message,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Typography,
    Upload,
} from 'antd'

import fetch from 'auth/FetchInterceptor'
import UploadService from 'services/UploadService'
import { env } from 'configs/EnvironmentConfig'

const {
    Title,
    Text,
} = Typography

const {
    Option,
} = Select

// ======================================================
// API / IMAGENS
// ======================================================

const API_BASE_URL =
    env.API_ENDPOINT_URL

const getImageUrl = (
    value,
) => {
    if (!value) {
        return ''
    }

    if (
        value.startsWith(
            'http://',
        ) ||
        value.startsWith(
            'https://',
        ) ||
        value.startsWith(
            'data:',
        )
    ) {
        return value
    }

    return `${API_BASE_URL}${value}`
}

// ======================================================
// COMPONENTE
// ======================================================

const GameLobbySettings =
    () => {
        const [form] =
            Form.useForm()

        // ======================================================
        // STATES
        // ======================================================

        const [
            loading,
            setLoading,
        ] =
            useState(false)

        const [
            saving,
            setSaving,
        ] =
            useState(false)

        const [
            events,
            setEvents,
        ] =
            useState([])

        const [
            selectedEventId,
            setSelectedEventId,
        ] =
            useState(null)

        const [
            lobby,
            setLobby,
        ] =
            useState(null)

        const [
            uploadingLogo,
            setUploadingLogo,
        ] =
            useState(false)

        const [
            uploadingBackground,
            setUploadingBackground,
        ] =
            useState(false)

        const [
            cards,
            setCards,
        ] = useState([])

        const [
            games,
            setGames,
        ] = useState([])

        const [
            cardModalOpen,
            setCardModalOpen,
        ] = useState(false)

        const [
            editingCard,
            setEditingCard,
        ] = useState(null)

        const [
            savingCard,
            setSavingCard,
        ] = useState(false)

        const [
            uploadingCardImage,
            setUploadingCardImage,
        ] = useState(false)

        const [
            cardForm,
        ] = Form.useForm()

        // ======================================================
        // WATCHERS
        // ======================================================

        const orientation =
            Form.useWatch(
                'orientation',
                form,
            ) ||
            'VERTICAL'

        const backgroundUrl =
            Form.useWatch(
                'backgroundUrl',
                form,
            ) ||
            ''

        const logoUrl =
            Form.useWatch(
                'logoUrl',
                form,
            ) ||
            ''

        const watchedValues =
            Form.useWatch(
                [],
                form,
            ) ||
            {}

        // ======================================================
        // CARREGAR EVENTOS
        // ======================================================

        const loadEvents =
            async () => {
                try {
                    const result =
                        await fetch({
                            url:
                                '/events',

                            method:
                                'get',
                        })

                    const list =
                        Array.isArray(
                            result,
                        )
                            ? result
                            : result?.data ||
                            result?.items ||
                            []

                    setEvents(
                        list,
                    )

                    if (
                        list.length >
                        0
                    ) {
                        setSelectedEventId(
                            list[0].id,
                        )
                    }
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao carregar eventos.',
                    )
                }
            }

        // ======================================================
        // CARREGAR LOBBY
        // ======================================================

        const loadLobby =
            async (
                eventId,
            ) => {
                if (
                    !eventId
                ) {
                    return
                }

                try {
                    setLoading(
                        true,
                    )

                    const result =
                        await fetch({
                            url:
                                `/game-lobby/event/${eventId}`,

                            method:
                                'get',
                        })

                    setLobby(
                        result,
                    )

                    setCards(
                        Array.isArray(
                            result?.cards,
                        )
                            ? result.cards
                            : [],
                    )

                    form.setFieldsValue(
                        {
                            isEnabled:
                                result?.isEnabled ??
                                true,

                            title:
                                result?.title ||
                                'ESCOLHA SEU JOGO!',

                            subtitle:
                                result?.subtitle ||
                                'Divirta-se e boa sorte!',

                            footerText:
                                result?.footerText ||
                                'Escolha um jogo para começar!',

                            backgroundColor:
                                result?.backgroundColor ||
                                '#081A73',

                            backgroundUrl:
                                result?.backgroundUrl ||
                                '',

                            showLogo:
                                result?.showLogo ??
                                true,

                            logoUrl:
                                result?.logoUrl ||
                                '',

                            logoWidth:
                                result?.logoWidth ||
                                300,

                            orientation:
                                result?.orientation ||
                                'VERTICAL',

                            cardGap:
                                result?.cardGap ??
                                32,

                            cardBorderRadius:
                                result?.cardBorderRadius ??
                                40,

                            cardShowShadow:
                                result?.cardShowShadow ??
                                true,

                            fullscreenEnabled:
                                result?.fullscreenEnabled ??
                                true,
                        },
                    )
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao carregar a página inicial.',
                    )
                } finally {
                    setLoading(
                        false,
                    )
                }
            }

        const loadGames =
            async (
                eventId,
            ) => {
                if (
                    !eventId
                ) {
                    return
                }

                try {
                    const result =
                        await fetch({
                            url:
                                '/games',

                            method:
                                'get',
                        })

                    const list =
                        Array.isArray(
                            result,
                        )
                            ? result
                            : result?.data ||
                            result?.items ||
                            []

                    const filtered =
                        list.filter(
                            (
                                game,
                            ) =>
                                game.eventId ===
                                eventId,
                        )

                    setGames(
                        filtered,
                    )
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao carregar jogos do evento.',
                    )
                }
            }

        // ======================================================
        // INICIALIZAÇÃO
        // ======================================================

        useEffect(
            () => {
                loadEvents()
            },
            [],
        )

        useEffect(
            () => {
                if (
                    selectedEventId
                ) {
                    loadLobby(
                        selectedEventId,
                    )

                    loadGames(
                        selectedEventId,
                    )
                }
            },
            [
                selectedEventId,
            ],
        )

        // ======================================================
        // UPLOAD DA LOGO
        // ======================================================

        const uploadLogo =
            async ({
                file,
                onSuccess,
                onError,
            }) => {
                try {
                    setUploadingLogo(
                        true,
                    )

                    const response =
                        await UploadService.image(
                            file,
                        )

                    const data =
                        response?.data ||
                        response

                    if (
                        !data?.url
                    ) {
                        throw new Error(
                            'URL da imagem não retornada.',
                        )
                    }

                    form.setFieldsValue(
                        {
                            logoUrl:
                                data.url,
                        },
                    )

                    message.success(
                        'Logo enviada com sucesso.',
                    )

                    if (
                        onSuccess
                    ) {
                        onSuccess(
                            data,
                        )
                    }
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao enviar logo.',
                    )

                    if (
                        onError
                    ) {
                        onError(
                            error,
                        )
                    }
                } finally {
                    setUploadingLogo(
                        false,
                    )
                }
            }

        // ======================================================
        // UPLOAD DO FUNDO
        // ======================================================

        const uploadBackground =
            async ({
                file,
                onSuccess,
                onError,
            }) => {
                try {
                    setUploadingBackground(
                        true,
                    )

                    const response =
                        await UploadService.image(
                            file,
                        )

                    const data =
                        response?.data ||
                        response

                    if (
                        !data?.url
                    ) {
                        throw new Error(
                            'URL da imagem não retornada.',
                        )
                    }

                    form.setFieldsValue(
                        {
                            backgroundUrl:
                                data.url,
                        },
                    )

                    message.success(
                        'Imagem de fundo enviada com sucesso.',
                    )

                    if (
                        onSuccess
                    ) {
                        onSuccess(
                            data,
                        )
                    }
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao enviar imagem de fundo.',
                    )

                    if (
                        onError
                    ) {
                        onError(
                            error,
                        )
                    }
                } finally {
                    setUploadingBackground(
                        false,
                    )
                }
            }

        // ======================================================
        // REMOVER FUNDO
        // ======================================================

        const removeBackground =
            () => {
                form.setFieldsValue(
                    {
                        backgroundUrl:
                            '',
                    },
                )
            }

        // ======================================================
        // REMOVER LOGO
        // ======================================================

        const removeLogo =
            () => {
                form.setFieldsValue(
                    {
                        logoUrl:
                            '',
                    },
                )
            }

        const openNewCard =
            () => {
                setEditingCard(
                    null,
                )

                cardForm.resetFields()

                cardForm.setFieldsValue(
                    {
                        isEnabled:
                            true,

                        title:
                            '',

                        description:
                            '',

                        buttonText:
                            'JOGAR',

                        backgroundColor:
                            '#1677ff',

                        textColor:
                            '#ffffff',

                        sortOrder:
                            cards.length,

                        gameId:
                            undefined,

                        imageUrl:
                            '',
                    },
                )

                setCardModalOpen(
                    true,
                )
            }

        const openEditCard =
            (
                card,
            ) => {
                setEditingCard(
                    card,
                )

                cardForm.setFieldsValue(
                    {
                        gameId:
                            card.gameId ||
                            undefined,

                        isEnabled:
                            card.isEnabled ??
                            true,

                        title:
                            card.title ||
                            '',

                        description:
                            card.description ||
                            '',

                        imageUrl:
                            card.imageUrl ||
                            '',

                        buttonText:
                            card.buttonText ||
                            'JOGAR',

                        backgroundColor:
                            card.backgroundColor ||
                            '#1677ff',

                        textColor:
                            card.textColor ||
                            '#ffffff',

                        sortOrder:
                            card.sortOrder ??
                            0,
                    },
                )

                setCardModalOpen(
                    true,
                )
            }
        const uploadCardImage =
            async ({
                file,
                onSuccess,
                onError,
            }) => {
                try {
                    setUploadingCardImage(
                        true,
                    )

                    const response =
                        await UploadService.image(
                            file,
                        )

                    const data =
                        response?.data ||
                        response

                    if (
                        !data?.url
                    ) {
                        throw new Error(
                            'URL da imagem não retornada.',
                        )
                    }

                    cardForm.setFieldsValue(
                        {
                            imageUrl:
                                data.url,
                        },
                    )

                    message.success(
                        'Imagem do card enviada com sucesso.',
                    )

                    if (
                        onSuccess
                    ) {
                        onSuccess(
                            data,
                        )
                    }
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao enviar imagem do card.',
                    )

                    if (
                        onError
                    ) {
                        onError(
                            error,
                        )
                    }
                } finally {
                    setUploadingCardImage(
                        false,
                    )
                }
            }
        const saveCard =
            async () => {
                if (
                    !selectedEventId
                ) {
                    return
                }

                try {
                    const values =
                        await cardForm.validateFields()

                    setSavingCard(
                        true,
                    )

                    if (
                        editingCard
                    ) {
                        await fetch({
                            url:
                                `/game-lobby/cards/${editingCard.id}`,

                            method:
                                'put',

                            data:
                                values,
                        })
                    } else {
                        await fetch({
                            url:
                                `/game-lobby/event/${selectedEventId}/cards`,

                            method:
                                'post',

                            data:
                                values,
                        })
                    }

                    message.success(
                        editingCard
                            ? 'Card atualizado com sucesso.'
                            : 'Card criado com sucesso.',
                    )

                    setCardModalOpen(
                        false,
                    )

                    setEditingCard(
                        null,
                    )

                    await loadLobby(
                        selectedEventId,
                    )
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao salvar card.',
                    )
                } finally {
                    setSavingCard(
                        false,
                    )
                }
            }
        const deleteCard =
            async (
                cardId,
            ) => {
                try {
                    await fetch({
                        url:
                            `/game-lobby/cards/${cardId}`,

                        method:
                            'delete',
                    })

                    message.success(
                        'Card removido com sucesso.',
                    )

                    await loadLobby(
                        selectedEventId,
                    )
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao remover card.',
                    )
                }
            }
        // ======================================================
        // SALVAR
        // ======================================================

        const handleSave =
            async () => {
                if (
                    !selectedEventId
                ) {
                    message.warning(
                        'Selecione um evento.',
                    )

                    return
                }

                try {
                    const values =
                        await form.validateFields()

                    setSaving(
                        true,
                    )

                    const result =
                        await fetch({
                            url:
                                `/game-lobby/event/${selectedEventId}`,

                            method:
                                'put',

                            data:
                                values,
                        })

                    setLobby(
                        result,
                    )

                    form.setFieldsValue(
                        {
                            ...values,
                            ...result,
                        },
                    )

                    message.success(
                        'Página inicial salva com sucesso.',
                    )
                } catch (
                error
                ) {
                    console.error(
                        error,
                    )

                    message.error(
                        'Erro ao salvar a página inicial.',
                    )
                } finally {
                    setSaving(
                        false,
                    )
                }
            }

        // ======================================================
        // RESOLUÇÃO DO PREVIEW
        // ======================================================

        const previewResolution =
            useMemo(
                () => {
                    if (
                        orientation ===
                        'HORIZONTAL'
                    ) {
                        return {
                            width:
                                1920,

                            height:
                                1080,
                        }
                    }

                    return {
                        width:
                            1080,

                        height:
                            1920,
                    }
                },
                [
                    orientation,
                ],
            )

        // ======================================================
        // TAMANHO VISUAL DO PREVIEW
        // ======================================================

        const previewScale =
            orientation ===
                'HORIZONTAL'
                ? 0.28
                : 0.24

        const previewWidth =
            previewResolution.width *
            previewScale

        const previewHeight =
            previewResolution.height *
            previewScale

        // ======================================================
        // VALORES DO FORM
        // ======================================================

        const values =
            watchedValues ||
            {}

        // ======================================================
        // RENDER
        // ======================================================

        return (
            <div>
                {/* ==================================================
            CABEÇALHO
        ================================================== */}

                <div
                    style={{
                        marginBottom:
                            24,
                    }}
                >
                    <Title
                        level={
                            2
                        }
                        style={{
                            marginBottom:
                                4,
                        }}
                    >
                        Página Inicial
                        dos Jogos
                    </Title>

                    <Text
                        type="secondary"
                    >
                        Configure
                        a tela onde
                        o visitante
                        escolhe qual
                        jogo deseja
                        jogar.
                    </Text>
                </div>

                {/* ==================================================
            EVENTO
        ================================================== */}

                <Card
                    style={{
                        marginBottom:
                            24,
                    }}
                >
                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                    >
                        <Col
                            xs={
                                24
                            }
                            md={
                                12
                            }
                        >
                            <Text
                                strong
                            >
                                Evento
                            </Text>

                            <Select
                                style={{
                                    width:
                                        '100%',
                                    marginTop:
                                        8,
                                }}
                                value={
                                    selectedEventId
                                }
                                onChange={
                                    setSelectedEventId
                                }
                                placeholder="Selecione o evento"
                            >
                                {events.map(
                                    (
                                        event,
                                    ) => (
                                        <Option
                                            key={
                                                event.id
                                            }
                                            value={
                                                event.id
                                            }
                                        >
                                            {
                                                event.name
                                            }
                                        </Option>
                                    ),
                                )}
                            </Select>
                        </Col>

                        <Col
                            xs={
                                24
                            }
                            md={
                                12
                            }
                            style={{
                                display:
                                    'flex',
                                alignItems:
                                    'flex-end',
                            }}
                        >
                            <Button
                                type="default"
                                size="large"
                                disabled={
                                    !selectedEventId
                                }
                                onClick={() => {
                                    const selectedEvent =
                                        events.find(
                                            (
                                                item,
                                            ) =>
                                                item.id ===
                                                selectedEventId,
                                        )

                                    if (
                                        !selectedEvent?.slug
                                    ) {
                                        message.warning(
                                            'Este evento não possui slug.',
                                        )

                                        return
                                    }

                                    window.open(
                                        `/lobby/${selectedEvent.slug}`,
                                        '_blank',
                                    )
                                }}
                            >
                                Abrir Página
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* ==================================================
            CONTEÚDO
        ================================================== */}

                <Spin
                    spinning={
                        loading
                    }
                >
                    <Row
                        gutter={[
                            24,
                            24,
                        ]}
                    >
                        {/* ==============================================
                CONFIGURAÇÕES
            ============================================== */}

                        <Col
                            xs={
                                24
                            }
                            xl={
                                12
                            }
                        >
                            <Form
                                form={
                                    form
                                }
                                layout="vertical"
                            >
                                {/* ==========================================
                    GERAL
                ========================================== */}

                                <Card
                                    title="Configuração"
                                    style={{
                                        marginBottom:
                                            24,
                                    }}
                                >
                                    <Form.Item
                                        name="isEnabled"
                                        label="Página inicial ativa"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>

                                    <Form.Item
                                        name="orientation"
                                        label="Formato da tela"
                                    >
                                        <Radio.Group>
                                            <Space
                                                direction="vertical"
                                            >
                                                <Radio
                                                    value="VERTICAL"
                                                >
                                                    Vertical
                                                    —
                                                    1080 × 1920
                                                </Radio>

                                                <Radio
                                                    value="HORIZONTAL"
                                                >
                                                    Horizontal
                                                    —
                                                    1920 × 1080
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </Form.Item>
                                </Card>

                                {/* ==========================================
                    TEXTOS
                ========================================== */}

                                <Card
                                    title="Textos"
                                    style={{
                                        marginBottom:
                                            24,
                                    }}
                                >
                                    <Form.Item
                                        name="title"
                                        label="Título"
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        name="subtitle"
                                        label="Subtítulo"
                                    >
                                        <Input />
                                    </Form.Item>

                                    <Form.Item
                                        name="footerText"
                                        label="Texto inferior"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Card>

                                {/* ==========================================
                    FUNDO
                ========================================== */}

                                <Card
                                    title="Fundo"
                                    style={{
                                        marginBottom:
                                            24,
                                    }}
                                >
                                    <Form.Item
                                        name="backgroundColor"
                                        label="Cor do fundo"
                                    >
                                        <Input
                                            type="color"
                                            style={{
                                                width:
                                                    100,
                                            }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Imagem de fundo"
                                    >
                                        <Space
                                            direction="vertical"
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        >
                                            <Form.Item
                                                name="backgroundUrl"
                                                noStyle
                                            >
                                                <Input
                                                    readOnly
                                                    placeholder="Nenhuma imagem enviada"
                                                />
                                            </Form.Item>

                                            <Space
                                                wrap
                                            >
                                                <Upload
                                                    accept="image/*"
                                                    showUploadList={
                                                        false
                                                    }
                                                    customRequest={
                                                        uploadBackground
                                                    }
                                                >
                                                    <Button
                                                        icon={
                                                            <UploadOutlined />
                                                        }
                                                        loading={
                                                            uploadingBackground
                                                        }
                                                    >
                                                        Enviar imagem
                                                    </Button>
                                                </Upload>

                                                <Button
                                                    danger
                                                    icon={
                                                        <DeleteOutlined />
                                                    }
                                                    disabled={
                                                        !backgroundUrl
                                                    }
                                                    onClick={
                                                        removeBackground
                                                    }
                                                >
                                                    Remover
                                                </Button>
                                            </Space>

                                            {backgroundUrl && (
                                                <Image
                                                    src={getImageUrl(
                                                        backgroundUrl,
                                                    )}
                                                    width={
                                                        240
                                                    }
                                                    style={{
                                                        borderRadius:
                                                            10,
                                                    }}
                                                />
                                            )}
                                        </Space>
                                    </Form.Item>
                                </Card>

                                {/* ==========================================
                    LOGO
                ========================================== */}

                                <Card
                                    title="Logo"
                                    style={{
                                        marginBottom:
                                            24,
                                    }}
                                >
                                    <Form.Item
                                        name="showLogo"
                                        label="Mostrar logo"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>

                                    <Form.Item
                                        label="Arquivo da logo"
                                    >
                                        <Space
                                            direction="vertical"
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        >
                                            <Form.Item
                                                name="logoUrl"
                                                noStyle
                                            >
                                                <Input
                                                    readOnly
                                                    placeholder="Nenhuma logo enviada"
                                                />
                                            </Form.Item>

                                            <Space
                                                wrap
                                            >
                                                <Upload
                                                    accept="image/*"
                                                    showUploadList={
                                                        false
                                                    }
                                                    customRequest={
                                                        uploadLogo
                                                    }
                                                >
                                                    <Button
                                                        icon={
                                                            <UploadOutlined />
                                                        }
                                                        loading={
                                                            uploadingLogo
                                                        }
                                                    >
                                                        Enviar logo
                                                    </Button>
                                                </Upload>

                                                <Button
                                                    danger
                                                    icon={
                                                        <DeleteOutlined />
                                                    }
                                                    disabled={
                                                        !logoUrl
                                                    }
                                                    onClick={
                                                        removeLogo
                                                    }
                                                >
                                                    Remover
                                                </Button>
                                            </Space>

                                            {logoUrl && (
                                                <Image
                                                    src={getImageUrl(
                                                        logoUrl,
                                                    )}
                                                    width={
                                                        180
                                                    }
                                                />
                                            )}
                                        </Space>
                                    </Form.Item>

                                    <Form.Item
                                        name="logoWidth"
                                        label="Largura da logo"
                                    >
                                        <InputNumber
                                            min={
                                                50
                                            }
                                            max={
                                                1000
                                            }
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Card>

                                {/* ==========================================
                    CARDS
                ========================================== */}

                                <Card
                                    title="Cards"
                                    style={{
                                        marginBottom:
                                            24,
                                    }}
                                >
                                    <Form.Item
                                        name="cardGap"
                                        label="Espaçamento"
                                    >
                                        <InputNumber
                                            min={
                                                0
                                            }
                                            max={
                                                200
                                            }
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="cardBorderRadius"
                                        label="Arredondamento"
                                    >
                                        <InputNumber
                                            min={
                                                0
                                            }
                                            max={
                                                200
                                            }
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="cardShowShadow"
                                        label="Sombra dos cards"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>

                                    <Form.Item
                                        name="fullscreenEnabled"
                                        label="Tela cheia"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                    <Divider />

                                    <div
                                        style={{
                                            display:
                                                'flex',

                                            justifyContent:
                                                'space-between',

                                            alignItems:
                                                'center',

                                            marginBottom:
                                                16,
                                        }}
                                    >
                                        <Text strong>
                                            Jogos da Página Inicial
                                        </Text>

                                        <Button
                                            type="primary"
                                            icon={
                                                <PlusOutlined />
                                            }
                                            onClick={
                                                openNewCard
                                            }
                                        >
                                            Adicionar jogo
                                        </Button>
                                    </div>

                                    <Space
                                        direction="vertical"
                                        style={{
                                            width:
                                                '100%',
                                        }}
                                    >
                                        {cards.length ===
                                            0 ? (
                                            <Text
                                                type="secondary"
                                            >
                                                Nenhum jogo adicionado.
                                            </Text>
                                        ) : (
                                            cards.map(
                                                (
                                                    card,
                                                ) => (
                                                    <Card
                                                        key={
                                                            card.id
                                                        }
                                                        size="small"
                                                        style={{
                                                            width:
                                                                '100%',
                                                        }}
                                                    >
                                                        <Row
                                                            gutter={
                                                                12
                                                            }
                                                            align="middle"
                                                        >
                                                            <Col
                                                                flex="80px"
                                                            >
                                                                {card.imageUrl ? (
                                                                    <Image
                                                                        src={getImageUrl(
                                                                            card.imageUrl,
                                                                        )}
                                                                        width={
                                                                            70
                                                                        }
                                                                        height={
                                                                            70
                                                                        }
                                                                        style={{
                                                                            objectFit:
                                                                                'cover',

                                                                            borderRadius:
                                                                                8,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        style={{
                                                                            width:
                                                                                70,

                                                                            height:
                                                                                70,

                                                                            background:
                                                                                card.backgroundColor ||
                                                                                '#1677ff',

                                                                            borderRadius:
                                                                                8,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Col>

                                                            <Col flex="auto">
                                                                <Text strong>
                                                                    {
                                                                        card.title
                                                                    }
                                                                </Text>

                                                                <div>
                                                                    <Text
                                                                        type="secondary"
                                                                    >
                                                                        {card.game?.name ||
                                                                            'Sem jogo vinculado'}
                                                                    </Text>
                                                                </div>
                                                            </Col>

                                                            <Col>
                                                                <Space>
                                                                    <Button
                                                                        icon={
                                                                            <EditOutlined />
                                                                        }
                                                                        onClick={() =>
                                                                            openEditCard(
                                                                                card,
                                                                            )
                                                                        }
                                                                    />

                                                                    <Button
                                                                        danger
                                                                        icon={
                                                                            <DeleteOutlined />
                                                                        }
                                                                        onClick={() =>
                                                                            deleteCard(
                                                                                card.id,
                                                                            )
                                                                        }
                                                                    />
                                                                </Space>
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                ),
                                            )
                                        )}
                                    </Space>
                                </Card>

                                {/* ==========================================
                    SALVAR
                ========================================== */}

                                <Button
                                    type="primary"
                                    size="large"
                                    loading={
                                        saving
                                    }
                                    onClick={
                                        handleSave
                                    }
                                    block
                                >
                                    Salvar Página
                                    Inicial
                                </Button>
                            </Form>
                        </Col>

                        {/* ==============================================
                PREVIEW
            ============================================== */}

                        <Col
                            xs={
                                24
                            }
                            xl={
                                12
                            }
                        >
                            <Card
                                title={`Preview — ${orientation ===
                                    'HORIZONTAL'
                                    ? '1920 × 1080'
                                    : '1080 × 1920'
                                    }`}
                            >
                                <div
                                    style={{
                                        width:
                                            '100%',

                                        display:
                                            'flex',

                                        justifyContent:
                                            'center',

                                        alignItems:
                                            'flex-start',

                                        overflow:
                                            'auto',

                                        padding:
                                            20,

                                        background:
                                            '#f0f2f5',

                                        borderRadius:
                                            8,
                                    }}
                                >
                                    <div
                                        style={{
                                            position:
                                                'relative',

                                            width:
                                                previewWidth,

                                            minWidth:
                                                previewWidth,

                                            height:
                                                previewHeight,

                                            minHeight:
                                                previewHeight,

                                            backgroundColor:
                                                values.backgroundColor ||
                                                '#081A73',

                                            backgroundImage:
                                                backgroundUrl
                                                    ? `url(${getImageUrl(
                                                        backgroundUrl,
                                                    )})`
                                                    : 'none',

                                            backgroundSize:
                                                'cover',

                                            backgroundPosition:
                                                'center',

                                            backgroundRepeat:
                                                'no-repeat',

                                            overflow:
                                                'hidden',

                                            display:
                                                'flex',

                                            flexDirection:
                                                'column',

                                            alignItems:
                                                'center',

                                            justifyContent:
                                                'flex-start',

                                            textAlign:
                                                'center',

                                            padding:
                                                orientation ===
                                                    'HORIZONTAL'
                                                    ? 30
                                                    : 24,

                                            boxSizing:
                                                'border-box',

                                            boxShadow:
                                                '0 10px 30px rgba(0,0,0,.25)',
                                        }}
                                    >
                                        {/* ======================================
                        OVERLAY DECORATIVO
                    ====================================== */}

                                        <div
                                            style={{
                                                position:
                                                    'absolute',

                                                inset:
                                                    0,

                                                background:
                                                    'linear-gradient(180deg, rgba(7,21,105,.10) 0%, rgba(10,8,80,.25) 100%)',

                                                pointerEvents:
                                                    'none',
                                            }}
                                        />

                                        {/* ======================================
                        CONTEÚDO
                    ====================================== */}

                                        <div
                                            style={{
                                                position:
                                                    'relative',

                                                zIndex:
                                                    2,

                                                width:
                                                    '100%',

                                                height:
                                                    '100%',

                                                display:
                                                    'flex',

                                                flexDirection:
                                                    'column',

                                                alignItems:
                                                    'center',
                                            }}
                                        >
                                            {/* LOGO */}

                                            {values.showLogo &&
                                                logoUrl && (
                                                    <img
                                                        src={getImageUrl(
                                                            logoUrl,
                                                        )}
                                                        alt="Logo"
                                                        style={{
                                                            width:
                                                                Math.max(
                                                                    50,
                                                                    Number(
                                                                        values.logoWidth ||
                                                                        300,
                                                                    ) *
                                                                    previewScale,
                                                                ),

                                                            maxWidth:
                                                                '80%',

                                                            maxHeight:
                                                                orientation ===
                                                                    'VERTICAL'
                                                                    ? 90
                                                                    : 70,

                                                            objectFit:
                                                                'contain',

                                                            marginBottom:
                                                                18,
                                                        }}
                                                    />
                                                )}

                                            {/* TÍTULO */}

                                            <div
                                                style={{
                                                    color:
                                                        '#ffffff',

                                                    fontWeight:
                                                        900,

                                                    fontSize:
                                                        orientation ===
                                                            'VERTICAL'
                                                            ? 26
                                                            : 24,

                                                    lineHeight:
                                                        1.1,

                                                    marginTop:
                                                        10,

                                                    textShadow:
                                                        '0 3px 8px rgba(0,0,0,.35)',
                                                }}
                                            >
                                                {values.title ||
                                                    'ESCOLHA SEU JOGO!'}
                                            </div>

                                            {/* SUBTÍTULO */}

                                            <div
                                                style={{
                                                    color:
                                                        '#ffffff',

                                                    opacity:
                                                        0.95,

                                                    fontSize:
                                                        14,

                                                    marginTop:
                                                        8,

                                                    marginBottom:
                                                        orientation ===
                                                            'VERTICAL'
                                                            ? 28
                                                            : 24,

                                                    textShadow:
                                                        '0 2px 6px rgba(0,0,0,.3)',
                                                }}
                                            >
                                                {values.subtitle ||
                                                    'Divirta-se e boa sorte!'}
                                            </div>

                                            {/* ==================================
                      {/* ==================================
    CARDS
================================== */}

                                            <div
                                                style={{
                                                    display:
                                                        'flex',

                                                    flexDirection:
                                                        orientation ===
                                                            'VERTICAL'
                                                            ? 'column'
                                                            : 'row',

                                                    justifyContent:
                                                        'center',

                                                    alignItems:
                                                        'center',

                                                    gap:
                                                        Math.max(
                                                            8,
                                                            Number(
                                                                values.cardGap ||
                                                                32,
                                                            ) *
                                                            previewScale,
                                                        ),

                                                    width:
                                                        '100%',
                                                }}
                                            >
                                                {cards.filter(
                                                    (
                                                        card,
                                                    ) =>
                                                        card.isEnabled,
                                                ).length === 0 ? (
                                                    /* ==================================
                                                        NENHUM CARD CONFIGURADO
                                                    ================================== */

                                                    <div
                                                        style={{
                                                            width:
                                                                orientation ===
                                                                    'VERTICAL'
                                                                    ? '88%'
                                                                    : '70%',

                                                            padding:
                                                                30,

                                                            color:
                                                                '#ffffff',

                                                            background:
                                                                'rgba(255,255,255,.12)',

                                                            border:
                                                                '1px dashed rgba(255,255,255,.35)',

                                                            borderRadius:
                                                                16,

                                                            textAlign:
                                                                'center',

                                                            fontSize:
                                                                14,

                                                            fontWeight:
                                                                600,
                                                        }}
                                                    >
                                                        Nenhum jogo configurado.
                                                    </div>
                                                ) : (
                                                    cards
                                                        .filter(
                                                            (
                                                                card,
                                                            ) =>
                                                                card.isEnabled,
                                                        )
                                                        .sort(
                                                            (
                                                                a,
                                                                b,
                                                            ) =>
                                                                (a.sortOrder ||
                                                                    0) -
                                                                (b.sortOrder ||
                                                                    0),
                                                        )
                                                        .map(
                                                            (
                                                                card,
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        card.id
                                                                    }
                                                                    style={{
                                                                        width:
                                                                            orientation ===
                                                                                'VERTICAL'
                                                                                ? '88%'
                                                                                : '30%',

                                                                        height:
                                                                            orientation ===
                                                                                'VERTICAL'
                                                                                ? 130
                                                                                : 160,

                                                                        backgroundColor:
                                                                            card.backgroundColor ||
                                                                            '#1677ff',

                                                                        backgroundImage:
                                                                            card.imageUrl
                                                                                ? `url(${getImageUrl(
                                                                                    card.imageUrl,
                                                                                )})`
                                                                                : 'none',

                                                                        backgroundSize:
                                                                            'cover',

                                                                        backgroundPosition:
                                                                            'center',

                                                                        backgroundRepeat:
                                                                            'no-repeat',

                                                                        borderRadius:
                                                                            Math.max(
                                                                                8,
                                                                                Number(
                                                                                    values.cardBorderRadius ||
                                                                                    40,
                                                                                ) *
                                                                                previewScale,
                                                                            ),

                                                                        color:
                                                                            card.textColor ||
                                                                            '#ffffff',

                                                                        display:
                                                                            'flex',

                                                                        flexDirection:
                                                                            'column',

                                                                        justifyContent:
                                                                            'flex-end',

                                                                        alignItems:
                                                                            'center',

                                                                        position:
                                                                            'relative',

                                                                        overflow:
                                                                            'hidden',

                                                                        padding:
                                                                            14,

                                                                        boxSizing:
                                                                            'border-box',

                                                                        boxShadow:
                                                                            values.cardShowShadow
                                                                                ? '0 8px 20px rgba(0,0,0,.25)'
                                                                                : 'none',
                                                                    }}
                                                                >
                                                                    {/* ==================================
                            SOMBRA SOBRE A IMAGEM
                        ================================== */}

                                                                    {card.imageUrl && (
                                                                        <div
                                                                            style={{
                                                                                position:
                                                                                    'absolute',

                                                                                inset:
                                                                                    0,

                                                                                background:
                                                                                    'linear-gradient(180deg, rgba(0,0,0,.02) 20%, rgba(0,0,0,.15) 50%, rgba(0,0,0,.78) 100%)',

                                                                                zIndex:
                                                                                    1,

                                                                                pointerEvents:
                                                                                    'none',
                                                                            }}
                                                                        />
                                                                    )}

                                                                    {/* ==================================
                            CONTEÚDO DO CARD
                        ================================== */}

                                                                    <div
                                                                        style={{
                                                                            position:
                                                                                'relative',

                                                                            zIndex:
                                                                                2,

                                                                            width:
                                                                                '100%',

                                                                            display:
                                                                                'flex',

                                                                            flexDirection:
                                                                                'column',

                                                                            alignItems:
                                                                                'center',

                                                                            justifyContent:
                                                                                'flex-end',

                                                                            textAlign:
                                                                                'center',
                                                                        }}
                                                                    >
                                                                        {/* TÍTULO */}

                                                                        <div
                                                                            style={{
                                                                                color:
                                                                                    card.textColor ||
                                                                                    '#ffffff',

                                                                                fontSize:
                                                                                    orientation ===
                                                                                        'VERTICAL'
                                                                                        ? 18
                                                                                        : 16,

                                                                                fontWeight:
                                                                                    900,

                                                                                lineHeight:
                                                                                    1.1,

                                                                                textShadow:
                                                                                    card.imageUrl
                                                                                        ? '0 2px 6px rgba(0,0,0,.75)'
                                                                                        : 'none',
                                                                            }}
                                                                        >
                                                                            {
                                                                                card.title
                                                                            }
                                                                        </div>

                                                                        {/* DESCRIÇÃO */}

                                                                        {card.description && (
                                                                            <div
                                                                                style={{
                                                                                    color:
                                                                                        card.textColor ||
                                                                                        '#ffffff',

                                                                                    fontSize:
                                                                                        orientation ===
                                                                                            'VERTICAL'
                                                                                            ? 11
                                                                                            : 10,

                                                                                    fontWeight:
                                                                                        600,

                                                                                    lineHeight:
                                                                                        1.2,

                                                                                    opacity:
                                                                                        0.95,

                                                                                    marginTop:
                                                                                        5,

                                                                                    textShadow:
                                                                                        card.imageUrl
                                                                                            ? '0 2px 5px rgba(0,0,0,.75)'
                                                                                            : 'none',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    card.description
                                                                                }
                                                                            </div>
                                                                        )}

                                                                        {/* BOTÃO */}

                                                                        {card.buttonText && (
                                                                            <div
                                                                                style={{
                                                                                    display:
                                                                                        'inline-flex',

                                                                                    alignItems:
                                                                                        'center',

                                                                                    justifyContent:
                                                                                        'center',

                                                                                    minWidth:
                                                                                        80,

                                                                                    height:
                                                                                        26,

                                                                                    marginTop:
                                                                                        9,

                                                                                    padding:
                                                                                        '0 14px',

                                                                                    background:
                                                                                        '#ffffff',

                                                                                    color:
                                                                                        card.backgroundColor ||
                                                                                        '#1677ff',

                                                                                    borderRadius:
                                                                                        999,

                                                                                    fontSize:
                                                                                        10,

                                                                                    fontWeight:
                                                                                        900,

                                                                                    lineHeight:
                                                                                        1,

                                                                                    boxShadow:
                                                                                        '0 3px 8px rgba(0,0,0,.20)',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    card.buttonText
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ),
                                                        )
                                                )}
                                            </div>

                                            {/* ==================================
                          RODAPÉ
                      ================================== */}

                                            <div
                                                style={{
                                                    marginTop:
                                                        'auto',

                                                    color:
                                                        '#ffffff',

                                                    opacity:
                                                        0.9,

                                                    fontSize:
                                                        12,

                                                    paddingBottom:
                                                        5,

                                                    textShadow:
                                                        '0 2px 6px rgba(0,0,0,.35)',
                                                }}
                                            >
                                                {values.footerText ||
                                                    'Escolha um jogo para começar!'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {/* ==========================================
                  DADOS DA CONFIGURAÇÃO
              ========================================== */}

                            {lobby && (
                                <Card
                                    size="small"
                                    style={{
                                        marginTop:
                                            16,
                                    }}
                                >
                                    <Space
                                        direction="vertical"
                                        size={
                                            2
                                        }
                                    >
                                        <Text
                                            type="secondary"
                                        >
                                            Resolução
                                        </Text>

                                        <Text
                                            strong
                                        >
                                            {orientation ===
                                                'HORIZONTAL'
                                                ? '1920 × 1080'
                                                : '1080 × 1920'}
                                        </Text>

                                        <Text
                                            type="secondary"
                                        >
                                            Evento
                                        </Text>

                                        <Text>
                                            {
                                                events.find(
                                                    (
                                                        item,
                                                    ) =>
                                                        item.id ===
                                                        selectedEventId,
                                                )
                                                    ?.name
                                            }
                                        </Text>
                                    </Space>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </Spin>

                {/* ======================================================
                    MODAL — ADICIONAR / EDITAR JOGO
                ====================================================== */}

                <Modal
                    open={
                        cardModalOpen
                    }
                    title={
                        editingCard
                            ? 'Editar Card'
                            : 'Adicionar Jogo'
                    }
                    onCancel={() => {
                        setCardModalOpen(
                            false,
                        )

                        setEditingCard(
                            null,
                        )
                    }}
                    onOk={
                        saveCard
                    }
                    confirmLoading={
                        savingCard
                    }
                    okText="Salvar"
                    cancelText="Cancelar"
                    width={
                        700
                    }
                >
                    <Form
                        form={
                            cardForm
                        }
                        layout="vertical"
                    >
                        {/* ==========================================
                            JOGO
                        ========================================== */}

                        <Form.Item
                            name="gameId"
                            label="Jogo vinculado"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        'Selecione o jogo.',
                                },
                            ]}
                        >
                            <Select
                                placeholder="Selecione o jogo"
                                allowClear
                            >
                                {games.map(
                                    (
                                        game,
                                    ) => (
                                        <Option
                                            key={
                                                game.id
                                            }
                                            value={
                                                game.id
                                            }
                                        >
                                            {
                                                game.name
                                            }
                                            {' — '}
                                            {
                                                game.type
                                            }
                                        </Option>
                                    ),
                                )}
                            </Select>
                        </Form.Item>

                        {/* ==========================================
                            STATUS
                        ========================================== */}

                        <Form.Item
                            name="isEnabled"
                            label="Card ativo"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>

                        {/* ==========================================
                            TÍTULO
                        ========================================== */}

                        <Form.Item
                            name="title"
                            label="Título"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        'Informe o título.',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Ex.: JOGO DA MEMÓRIA"
                            />
                        </Form.Item>

                        {/* ==========================================
                            DESCRIÇÃO
                        ========================================== */}

                        <Form.Item
                            name="description"
                            label="Descrição"
                        >
                            <Input
                                placeholder="Ex.: Encontre os pares!"
                            />
                        </Form.Item>

                        {/* ==========================================
                            BOTÃO
                        ========================================== */}

                        <Form.Item
                            name="buttonText"
                            label="Texto do botão"
                        >
                            <Input
                                placeholder="JOGAR"
                            />
                        </Form.Item>

                        {/* ==========================================
                            CORES
                        ========================================== */}

                        <Row
                            gutter={
                                16
                            }
                        >
                            <Col
                                xs={
                                    24
                                }
                                md={
                                    12
                                }
                            >
                                <Form.Item
                                    name="backgroundColor"
                                    label="Cor do card"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width:
                                                '100%',
                                            height:
                                                40,
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={
                                    24
                                }
                                md={
                                    12
                                }
                            >
                                <Form.Item
                                    name="textColor"
                                    label="Cor do texto"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width:
                                                '100%',
                                            height:
                                                40,
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* ==========================================
                            ARTE DO CARD
                        ========================================== */}

                        <Form.Item
                            label="Arte do card"
                        >
                            <Form.Item
                                name="imageUrl"
                                noStyle
                            >
                                <Input
                                    readOnly
                                    placeholder="Nenhuma imagem enviada"
                                />
                            </Form.Item>

                            <div
                                style={{
                                    marginTop:
                                        12,
                                }}
                            >
                                <Upload
                                    accept="image/*"
                                    showUploadList={
                                        false
                                    }
                                    customRequest={
                                        uploadCardImage
                                    }
                                >
                                    <Button
                                        icon={
                                            <UploadOutlined />
                                        }
                                        loading={
                                            uploadingCardImage
                                        }
                                    >
                                        Enviar arte
                                    </Button>
                                </Upload>
                            </div>
                        </Form.Item>

                        {/* ==========================================
                            ORDEM
                        ========================================== */}

                        <Form.Item
                            name="sortOrder"
                            label="Ordem"
                        >
                            <InputNumber
                                min={
                                    0
                                }
                                style={{
                                    width:
                                        '100%',
                                }}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        )
    }

export default GameLobbySettings